/**
 * The Buddy panel beside the builder's week (ai-plan-builder R13 to R19; boards I, S, S2): the
 * pearl, „Asystent planu" and „Rozmowa o tym planie", the history and close buttons, the line that
 * says every change waits for the trainer, and the conversation itself, which is `BuddyChat` in its
 * panel layout: the same thread, cards and composer as the Buddy page.
 *
 * The panel holds its box from the first frame; the conversation's read waits inside it, gated, and
 * the thread, the unavailable state or the failure takes its place once.
 */

import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isTurnInFlight } from '~/entities/ai-conversation';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { BrandLoader } from '~/shared/ui/BrandLoader';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import { PearlMark } from '~/shared/ui/PearlMark';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { BuddyConversation, DraftForMessage } from '../lib/useBuddyConversation';

import { BuddyChat } from './BuddyChat';
import { HistoryGlyph } from './ChatGlyphs';
import type { ProposalHost } from './ProposalCard';
import surfaces from './surfaces.module.css';
import styles from './BuddyPanel.module.css';

export interface BuddyPanelProps {
  readonly buddy: BuddyConversation;
  /** The plan as a message carries it now (R13). */
  readonly draftForMessage: () => DraftForMessage;
  readonly proposalHost: ProposalHost;
  /** Buddy takes new work: the import is offered. */
  readonly offered: boolean;
  readonly onClose: () => void;
  /** The conversation on the Buddy page, its history beside it. */
  readonly onHistory: (conversationId: number) => void;
  /**
   * The conversation on the Buddy page, for what is done there: a draft built here (a spreadsheet's)
   * is handed to the builder from there, and an exercise outside the catalogue is added there, its
   * row (`row`) open when the page arrives.
   */
  readonly onOpenOnPage: (conversationId: number, row?: { readonly turnId: number; readonly name: string }) => void;
}

export function BuddyPanel({ buddy, draftForMessage, proposalHost, offered, onClose, onHistory, onOpenOnPage }: BuddyPanelProps) {
  const { t } = useTranslation();
  const { state } = buddy;
  const gate = useSkeletonGate(state.phase === 'pending');
  // The composer takes focus when the panel opens on its conversation.
  const [focusComposer] = useState(() => Date.now());
  const conversation = state.phase === 'ready' ? state.conversation : null;

  let content;
  if (gate.waiting) {
    content = gate.skeleton ? (
      <div className={styles.state}>
        <BrandLoader size="panel" label={t('web:aiPlan.assistant.loading')} />
      </div>
    ) : (
      <span className={hidden.srOnly}>{t('web:aiPlan.assistant.loading')}</span>
    );
  } else if (state.phase === 'ready') {
    content = (
      <div className={clsx(styles.chat, gate.reveal)}>
        <BuddyChat
          key={state.conversation.id}
          layout="panel"
          conversation={state.conversation}
          onSend={(intent) => buddy.send(intent, intent.kind === 'message' ? draftForMessage() : undefined)}
          onStop={buddy.stop}
          onDismissOutline={buddy.dismissOutline}
          deciding={buddy.deciding}
          exercises={{
            decide: (turnId, decisions) => buddy.decide(turnId, decisions).then((revision) => revision !== null),
            // The catalogue is written from the Buddy page, where the creator is: a row here offers
            // „Zdecyduj na stronie Buddy’ego", which opens the conversation there with that row open.
            catalogue: {
              kind: 'page',
              open: (turnId, name) => {
                onOpenOnPage(state.conversation.id, { turnId, name });
              },
            },
          }}
          onOpenDraft={() => {
            onOpenOnPage(state.conversation.id);
          }}
          sessionsOf={() => []}
          openSession={null}
          onOpenSession={() => undefined}
          returnFocusSession={null}
          focusComposer={focusComposer}
          onFeedback={buddy.rate}
          notice={buddy.notice}
          proposalHost={proposalHost}
          onDecideChanges={(turnId, version, decisions) => {
            void buddy.decideChanges(turnId, version, decisions);
          }}
          importer={offered ? { list: buddy.listFile, send: buddy.importFile } : null}
        />
      </div>
    );
  } else if (state.phase === 'failed') {
    content = (
      <div className={clsx(styles.state, gate.reveal)}>
        <p className={styles.stateText}>{t('web:aiPlan.assistant.failed')}</p>
        <button type="button" className={clsx(surfaces.button, surfaces.ghost)} onClick={state.retry}>
          {t('web:aiPlan.assistant.retry')}
        </button>
      </div>
    );
  } else {
    // Unavailable, or gone: the conversation was deleted or has expired.
    content = (
      <div className={clsx(styles.state, gate.reveal)}>
        <PearlMark className={styles.stateMark} />
        <p className={styles.stateText}>
          {state.phase === 'gone' ? t('web:aiPlan.gone.title') : t('web:aiPlan.assistant.unavailable')}
        </p>
      </div>
    );
  }

  return (
    <aside className={styles.panel} aria-label={t('web:aiPlan.assistant.title')} data-assistant-panel="">
      <div className={styles.head}>
        <PearlMark state={conversation?.turns.some(isTurnInFlight) === true ? 'work' : 'rest'} className={styles.headMark} />
        <div className={styles.headText}>
          <h2 className={styles.title}>{t('web:aiPlan.assistant.title')}</h2>
          <p className={styles.subtitle}>{t('web:aiPlan.assistant.subtitle')}</p>
        </div>
        <button
          type="button"
          className={styles.icon}
          aria-label={t('web:aiPlan.assistant.history')}
          aria-disabled={conversation === null ? 'true' : undefined}
          onClick={() => {
            if (conversation !== null) onHistory(conversation.id);
          }}
        >
          <HistoryGlyph className={styles.iconGlyph} />
        </button>
        <button type="button" className={styles.icon} aria-label={t('web:aiPlan.assistant.close')} onClick={onClose}>
          <CloseGlyph className={styles.iconGlyph} />
        </button>
      </div>
      <p className={styles.disclosure}>{t('web:aiPlan.assistant.disclosure')}</p>
      <div className={styles.body} aria-busy={gate.waiting ? 'true' : undefined}>
        {content}
      </div>
    </aside>
  );
}
